import { gmImagesState, LANGUAGES } from '../state/gmImagesState.js';
import { GmImagesService } from '../services/gmImagesService.js';
import { GmImagesUI } from '../components/gmImagesUI.js';
import { toast } from '../toast.js';
import { compressImage } from '../utils/imageCompression.js';
import { ModalManager } from '../utils/modalManager.js';
import { detectLangFromFilename, readFileAsDataURL } from '../utils/gmFilenameLang.js';

const LANG_LABELS = { en: 'EN', hi: 'HI', mr: 'MR', gu: 'GU' };
export class GmImagesController {
  constructor(db, storage, overlayManager) {
    this.service = new GmImagesService(db, storage);
    this.ui = new GmImagesUI();
    this.overlayManager = overlayManager;
    this.currentDate = null;
    this.onNavigateHome = null;
    this.onSubmitted = null;

    this.setupEventHandlers();
  }

  setNavigateHomeHandler(handler) {
    this.onNavigateHome = handler;
  }

  /** Called with the stored languages once an upload lands in the database. */
  setSubmittedHandler(handler) {
    this.onSubmitted = handler;
  }

  setupEventHandlers() {
    this.ui.setupFileHandlers((lang, file, previewUrl) => {
      gmImagesState.setState(lang, {
        file,
        preview: previewUrl
      });
      this.ui.updateUploadButtonStates();
    });

    document.getElementById('gmBatchFile')?.addEventListener('change', (e) => {
      this.handleBatchFiles(e.target.files);
      e.target.value = '';
    });

    // Clear All is bound via window.clearAllGmImages + onclick in HTML
    // to avoid missed listeners when the controller initializes.

    gmImagesState.subscribe(() => {
      this.ui.updateUploadButtonStates();
    });
  }

  /**
   * Distribute multiple selected files into language slots using filename codes:
   * en, hn/hi, mr, gj/gu.
   */
  async handleBatchFiles(fileList) {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const assigned = [];
    const skippedFilled = [];
    const unrecognized = [];
    const invalid = [];
    const duplicates = [];
    const seenLangs = new Set();

    for (const file of files) {
      if (!file.type?.startsWith('image/')) {
        invalid.push(file.name);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        invalid.push(`${file.name} (>10MB)`);
        continue;
      }

      const lang = detectLangFromFilename(file.name);
      if (!lang) {
        unrecognized.push(file.name);
        continue;
      }

      if (seenLangs.has(lang)) {
        duplicates.push(`${file.name} → ${LANG_LABELS[lang]}`);
        continue;
      }

      const state = gmImagesState.getState(lang);
      if (state.preview || state.existingUrl || state.file) {
        skippedFilled.push(`${file.name} → ${LANG_LABELS[lang]} (clear slot first)`);
        continue;
      }

      try {
        const previewUrl = await readFileAsDataURL(file);
        gmImagesState.setState(lang, { file, preview: previewUrl });
        this.ui.updatePreview(lang, previewUrl);
        seenLangs.add(lang);
        assigned.push(LANG_LABELS[lang]);
      } catch (err) {
        invalid.push(file.name);
        console.error('Batch read failed:', err);
      }
    }

    this.ui.updateUploadButtonStates();

    if (assigned.length > 0) {
      toast.success(`Filled ${assigned.join(', ')} from filenames`);
    }

    const notes = [];
    if (unrecognized.length) notes.push(`Unrecognized: ${unrecognized.join(', ')}`);
    if (skippedFilled.length) notes.push(`Already filled: ${skippedFilled.join('; ')}`);
    if (duplicates.length) notes.push(`Duplicate lang: ${duplicates.join('; ')}`);
    if (invalid.length) notes.push(`Skipped: ${invalid.join(', ')}`);

    if (notes.length > 0) {
      toast.warning(notes.join(' · '));
    } else if (assigned.length === 0) {
      toast.info('No images were assigned. Use filenames with en, hn, mr, or gj.');
    }
  }

  async loadExistingImages(dateString) {
    this.currentDate = dateString;
    gmImagesState.setDate(dateString);

    try {
      LANGUAGES.forEach(lang => {
        const preview = this.ui.previewElements[lang];
        if (preview) {
          preview.classList.remove('has-image');
          // Fills the slot so it keeps its 2:3 box while the image loads —
          // a bare spinner let the slot collapse and jump back.
          preview.innerHTML = '<span class="skel-fill" aria-busy="true" aria-label="Loading"></span>';
        }
      });

      const existingImages = await this.service.loadExistingImages(dateString);

      if (existingImages) {
        LANGUAGES.forEach(lang => {
          if (existingImages[lang]) {
            gmImagesState.setState(lang, {
              preview: existingImages[lang].downloadURL,
              existingUrl: existingImages[lang].downloadURL,
              storagePath: existingImages[lang].storagePath,
              file: null
            });
            this.ui.updatePreview(lang, existingImages[lang].downloadURL);
          } else {
            gmImagesState.resetState(lang);
            this.ui.clearPreview(lang);
          }
        });
      } else {
        LANGUAGES.forEach(lang => {
          gmImagesState.resetState(lang);
          this.ui.clearPreview(lang);
        });
      }

      this.ui.updateUploadButtonStates();
      console.log('✓ Finished loading GM images from Firestore');
    } catch (error) {
      console.error('Error loading GM images:', error);
      LANGUAGES.forEach(lang => {
        gmImagesState.resetState(lang);
        this.ui.clearPreview(lang);
      });
      this.ui.updateUploadButtonStates();
    }
  }

  hasPendingUpload(lang) {
    return gmImagesState.hasPendingUpload(lang);
  }

  /**
   * Update GM Images (bottom) — process all pending languages → Storage + gm_daily_images,
   * then reload. Success/failure modal + navigate home on success.
   */
  async uploadAllImages() {
    if (!this.currentDate) {
      toast.error('No date selected');
      return;
    }

    const submitBtn = document.getElementById('updateGmBtn');
    if (!submitBtn) return;

    const originalText = submitBtn.innerHTML;

    try {
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<div class="spinner inline-block mr-2"></div>Processing...';

      this.overlayManager.show('Uploading GM images...', {
        subtext: 'Compressing and uploading images',
        spinnerColor: '#9333ea'
      });

      const uploadPromises = [];
      const languageData = {};

      for (const lang of LANGUAGES) {
        const state = gmImagesState.getState(lang);

        if (state.file) {
          if (state.existingUrl) {
            await this.service.deleteImage(lang, state.storagePath, this.currentDate);
          }

          const compressedFile = await compressImage(state.file);

          const uploadPromise = this.service.uploadImage(lang, compressedFile, this.currentDate)
            .then(result => {
              languageData[lang] = result;
              gmImagesState.setState(lang, {
                preview: result.downloadURL,
                existingUrl: result.downloadURL,
                storagePath: result.storagePath,
                uploadedAt: new Date().toISOString(),
                file: null
              });
              return { language: lang, isNewFile: true };
            });

          uploadPromises.push(uploadPromise);
        } else if (state.preview && !state.existingUrl) {
          const uploadPromise = this.service.uploadFromDataURL(lang, state.preview, this.currentDate)
            .then(result => {
              languageData[lang] = result;
              gmImagesState.setState(lang, {
                preview: result.downloadURL,
                existingUrl: result.downloadURL,
                storagePath: result.storagePath,
                uploadedAt: new Date().toISOString(),
                file: null
              });
              return { language: lang, isNewFile: false };
            });

          uploadPromises.push(uploadPromise);
        }
      }

      let uploadResults = [];
      if (uploadPromises.length > 0) {
        console.log(`Waiting for ${uploadPromises.length} upload(s) to complete...`);
        uploadResults = await Promise.all(uploadPromises);
        console.log(`✓ All ${uploadResults.length} upload(s) completed successfully`);

        LANGUAGES.forEach(lang => {
          const fileInput = this.ui.fileInputElements[lang];
          if (fileInput) fileInput.value = '';
        });
      }

      submitBtn.innerHTML = '<div class="spinner inline-block mr-2"></div>Saving to database...';
      this.overlayManager.updateMessage('Saving to database...');

      const savedCount = await this.service.saveToFirestore(this.currentDate, languageData);

      await this.loadExistingImages(this.currentDate);

      this.overlayManager.hide();
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalText;
      this.ui.updateUploadButtonStates();

      if (savedCount > 0 || uploadResults.length > 0) {
        // A modal asking "Back to Main or stay?" made the operator decide what
        // to do before being told what had happened. The confirmation page
        // states what is stored and carries the same choices as real controls.
        if (typeof this.onSubmitted === 'function') {
          this.onSubmitted(this.currentDate, LANGUAGES.map(lang => ({
            lang,
            url: gmImagesState.getState(lang).existingUrl || null,
          })));
        }
      } else {
        toast.info('No new images to update. Choose files first, then click Update GM Images.');
      }

    } catch (error) {
      console.error('Error uploading GM images:', error);
      this.overlayManager.hide();
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalText;
      this.ui.updateUploadButtonStates();

      await this.ui.showResultModal({
        success: false,
        title: 'Update Failed',
        message: error.message || 'Failed to upload images. Please try again.',
        okText: 'Try Again'
      });
    }
  }

  async clearImage(lang) {
    if (!this.currentDate) {
      toast.error('No date selected');
      return;
    }

    try {
      this.overlayManager.show(`Clearing ${lang.toUpperCase()} image...`, {
        subtext: 'Removing from storage and database',
        spinnerColor: '#dc2626'
      });

      const state = gmImagesState.getState(lang);

      try {
        await this.service.deleteImage(lang, state.storagePath, this.currentDate);
      } catch (storageErr) {
        console.warn(`Storage delete warning for ${lang}:`, storageErr.message);
      }

      try {
        await this.service.removeFromFirestore(lang, this.currentDate);
      } catch (dbErr) {
        console.warn(`Firestore remove warning for ${lang}:`, dbErr.message);
      }

      gmImagesState.resetState(lang);
      this.ui.clearPreview(lang);
      this.ui.updateUploadButtonStates();

      this.overlayManager.hide();
      toast.success(`${lang.toUpperCase()} image cleared`);

    } catch (error) {
      console.error(`✗ Error clearing ${lang} image:`, error);
      this.overlayManager.hide();
      toast.error(`Failed to clear ${lang.toUpperCase()} image: ${error.message}`);
    }
  }

  async clearAllImages() {
    if (this._clearAllRunning) {
      toast.info('Clear already in progress...');
      return;
    }
    this._clearAllRunning = true;

    const clearBtn = document.getElementById('gmClearAllBtn');
    const originalClearHtml = clearBtn?.innerHTML;

    try {
      // Prefer controller date; fall back to the date picker on the page
      const dateFromPicker = document.getElementById('gmInlineDatePicker')?.value;
      if (!this.currentDate && dateFromPicker) {
        this.currentDate = dateFromPicker;
      }

      if (!this.currentDate) {
        toast.error('Please select a date first');
        return;
      }

      const confirmed = await ModalManager.showConfirm({
        title: 'Clear All Images?',
        message: `This will permanently delete all GM images (EN, HI, MR, GU) for ${this.currentDate} from storage and the database, and clear them from this page.`,
        confirmText: 'Clear All',
        cancelText: 'Cancel',
        variant: 'destructive'
      });

      if (!confirmed) {
        toast.info('Clear cancelled');
        return;
      }

      if (clearBtn) {
        clearBtn.disabled = true;
        clearBtn.innerHTML = '<div class="spinner inline-block mr-2"></div>Clearing...';
      }

      this.overlayManager.show('Clearing GM images...', {
        subtext: 'Removing images from storage and database',
        spinnerColor: '#dc2626'
      });

      const cleared = [];
      const warnings = [];

      // Always process ALL languages — do not skip based on in-memory state
      for (let i = 0; i < LANGUAGES.length; i++) {
        const lang = LANGUAGES[i];
        const state = gmImagesState.getState(lang);

        this.overlayManager.updateMessage(
          `Clearing ${lang.toUpperCase()}...`,
          `${i + 1}/${LANGUAGES.length}`
        );

        try {
          await this.service.deleteImage(lang, state?.storagePath || null, this.currentDate);
        } catch (storageErr) {
          console.warn(`Storage delete warning for ${lang}:`, storageErr);
          warnings.push(`${lang.toUpperCase()} storage: ${storageErr.message}`);
        }

        try {
          await this.service.removeFromFirestore(lang, this.currentDate);
        } catch (dbErr) {
          console.warn(`Firestore remove warning for ${lang}:`, dbErr);
          warnings.push(`${lang.toUpperCase()} db: ${dbErr.message}`);
        }

        gmImagesState.resetState(lang);
        this.ui.clearPreview(lang);
        cleared.push(lang);
      }

      // Extra safety: wipe DOM + localStorage even if state was empty
      this.ui.clearAllPreviews();
      gmImagesState.resetState();
      gmImagesState.clearLocalStorage();

      this.overlayManager.hide();

      if (clearBtn) {
        clearBtn.innerHTML = originalClearHtml || 'Clear All';
        clearBtn.disabled = false;
        clearBtn.removeAttribute('disabled');
      }
      this.ui.updateUploadButtonStates();

      if (warnings.length === 0) {
        await this.ui.showResultModal({
          success: true,
          title: 'Images Cleared',
          message: `All GM images for ${this.currentDate} were cleared from this page, storage, and gm_daily_images.`,
          okText: 'OK'
        });
      } else {
        await this.ui.showResultModal({
          success: true,
          title: 'Images Cleared (with warnings)',
          message: `Page cleared for ${cleared.map(l => l.toUpperCase()).join(', ')}. Some remote deletes had warnings: ${warnings.join('; ')}`,
          okText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error clearing all GM images:', error);
      this.overlayManager.hide();

      // Still force-clear the page so the UI isn't stuck
      try {
        this.ui.clearAllPreviews();
        gmImagesState.resetState();
        gmImagesState.clearLocalStorage();
      } catch (_) { /* ignore */ }

      if (clearBtn) {
        clearBtn.innerHTML = originalClearHtml || 'Clear All';
        clearBtn.disabled = false;
        clearBtn.removeAttribute('disabled');
      }
      // (was this.ui.updateSelectionUI() — a method GmImagesUI does not have,
      //  so it threw here and the failure modal below never showed.)
      this.ui.updateUploadButtonStates();

      await this.ui.showResultModal({
        success: false,
        title: 'Clear Failed',
        message: error.message || 'Failed to clear images. Please try again.',
        okText: 'Try Again'
      });
    } finally {
      this._clearAllRunning = false;
    }
  }

  resetPanel() {
    gmImagesState.resetState();
    gmImagesState.clearLocalStorage();
    this.ui.resetForm();
  }

  restorePreviews(expectedDate = null) {
    this.ui.restorePreviews(expectedDate || this.currentDate);
    this.ui.updateUploadButtonStates();
  }
}
